import { useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Bell,
  Briefcase,
  Building2,
  ChevronDown,
  FileBadge,
  LayoutDashboard,
  LogOut,
  Menu,
  MessageSquare,
  Search,
  Settings,
  ShieldCheck,
  ShieldQuestion,
  User,
  Users,
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Logo } from '@/components/shared/logo';
import { ThemeToggle } from '@/components/shared/theme-toggle';
import { ROUTES } from '@/lib/constants';
import { cn } from '@/lib/utils';
import { useLogout, useMe } from '@/features/auth/hooks';
import { useUnreadNotificationCount } from '@/features/notifications/hooks';
import { useUser } from '@/stores/auth-store';

interface NavItem {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
}

const candidateNav: NavItem[] = [
  { to: ROUTES.dashboard, icon: LayoutDashboard, label: 'Dashboard' },
  { to: ROUTES.profile, icon: User, label: 'Profile' },
  { to: ROUTES.candidateVerification, icon: ShieldCheck, label: 'Verification' },
  { to: ROUTES.myReport, icon: FileBadge, label: 'My Report' },
  { to: ROUTES.verificationConsents, icon: ShieldQuestion, label: 'Requests' },
  { to: ROUTES.messaging, icon: MessageSquare, label: 'Messaging' },
  { to: ROUTES.network, icon: Users, label: 'Network' },
  { to: ROUTES.jobs, icon: Briefcase, label: 'Jobs' },
];

const employerNav: NavItem[] = [
  { to: ROUTES.employer, icon: LayoutDashboard, label: 'Dashboard' },
  { to: ROUTES.employerProfile, icon: Building2, label: 'Company profile' },
  { to: ROUTES.employerDiscovery, icon: Search, label: 'Candidate discovery' },
  { to: ROUTES.employerVerificationRequests, icon: ShieldCheck, label: 'Verification requests' },
  { to: ROUTES.employerMessaging, icon: MessageSquare, label: 'Messaging' },
];

export function AppShell() {
  // The auth store persists the login-time user in localStorage; refresh it
  // from the server so identity changes (e.g. a rename) don't render stale.
  useMe();
  const user = useUser();
  const role = user?.role === 'employer' ? 'employer' : 'candidate';
  const nav = role === 'employer' ? employerNav : candidateNav;
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="grid min-h-dvh grid-cols-1 lg:grid-cols-[260px_1fr]">
      {/* Desktop sidebar */}
      <aside className="hidden border-e border-(--color-border) bg-(--color-card)/40 lg:sticky lg:top-0 lg:flex lg:h-dvh lg:flex-col">
        <div className="flex h-16 items-center px-5">
          <Link to={ROUTES.home}><Logo /></Link>
        </div>
        <SidebarNav nav={nav} />
        <UserBlock />
      </aside>

      <div className="flex min-h-dvh flex-col">
        <TopBar onOpenMenu={() => setMobileOpen(true)} />
        <main id="main-content" className="flex-1 px-4 py-6 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl animate-in">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Mobile drawer */}
      {mobileOpen ? (
        <div
          className="fixed inset-0 z-50 lg:hidden"
          role="dialog"
          aria-modal="true"
          onKeyDown={(e) => e.key === 'Escape' && setMobileOpen(false)}
        >
          <button
            className="absolute inset-0 bg-black/50"
            aria-label="Close menu"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="absolute inset-y-0 start-0 w-72 border-e border-(--color-border) bg-(--color-card) shadow-2xl">
            <div className="flex h-16 items-center px-5">
              <Link to={ROUTES.home} onClick={() => setMobileOpen(false)}><Logo /></Link>
            </div>
            <SidebarNav nav={nav} onNavigate={() => setMobileOpen(false)} />
            <UserBlock />
          </aside>
        </div>
      ) : null}
    </div>
  );
}

function SidebarNav({ nav, onNavigate }: { nav: NavItem[]; onNavigate?: () => void }) {
  return (
    <nav className="flex-1 overflow-y-auto px-3 py-2" aria-label="Sidebar">
      <ul className="space-y-0.5">
        {nav.map((item) => (
          <li key={item.to}>
            <NavLink
              to={item.to}
              onClick={onNavigate}
              end={item.to === ROUTES.dashboard || item.to === ROUTES.employer}
              className={({ isActive }) =>
                cn(
                  'group flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                  'text-(--color-muted-foreground) hover:bg-(--color-muted) hover:text-(--color-foreground)',
                  isActive && 'bg-(--color-accent) text-(--color-accent-foreground)',
                )
              }
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function TopBar({ onOpenMenu }: { onOpenMenu: () => void }) {
  const location = useLocation();
  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-(--color-border) bg-(--color-background)/85 px-4 backdrop-blur-md sm:px-6 lg:px-8">
      <Button variant="ghost" size="icon" className="lg:hidden" onClick={onOpenMenu} aria-label="Open navigation">
        <Menu />
      </Button>

      <div className="flex flex-1 items-center gap-2">
        <div className="relative max-w-md flex-1">
          <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-(--color-muted-foreground)" />
          <input
            type="search"
            placeholder="Search candidates, companies, cases…"
            className="h-10 w-full rounded-md border border-(--color-input) bg-(--color-background) ps-9 pe-3 text-sm shadow-sm placeholder:text-(--color-muted-foreground) focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--color-ring)"
            aria-label="Search"
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <ThemeToggle />
        <NotificationBell />
        <UserMenu />
      </div>
      {/* Surface current path for screen readers as a fallback breadcrumb */}
      <span className="sr-only">Current page: {location.pathname}</span>
    </header>
  );
}

/**
 * Bell with an unread badge (issue #103). The icon used to be identical whether
 * you had 0 or 40 unread notifications, so nobody clicked it. The count is
 * mirrored into the accessible name because the badge itself is aria-hidden —
 * a screen reader would otherwise read a bare number next to "Notifications".
 */
function NotificationBell() {
  const { data: unreadCount = 0 } = useUnreadNotificationCount();
  const hasUnread = unreadCount > 0;

  return (
    <Button
      variant="ghost"
      size="icon"
      className="relative"
      aria-label={hasUnread ? `Notifications (${unreadCount} unread)` : 'Notifications'}
      asChild
    >
      <Link to={ROUTES.notifications}>
        <Bell />
        {hasUnread ? (
          <span
            aria-hidden="true"
            data-testid="notification-unread-badge"
            className="absolute -top-0.5 -end-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-(--color-destructive) px-1 text-[10px] font-semibold leading-none text-(--color-destructive-foreground) ring-2 ring-(--color-background)"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        ) : null}
      </Link>
    </Button>
  );
}

function UserMenu() {
  const user = useUser();
  const logout = useLogout();
  const navigate = useNavigate();
  const name =
    [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.email || 'You';
  // An employer's "profile" is their company record, not a CV.
  const isEmployer = user?.role === 'employer';
  const profileRoute = isEmployer ? ROUTES.employerProfile : ROUTES.profile;
  const profileLabel = isEmployer ? 'Company profile' : 'Profile';
  const ProfileIcon = isEmployer ? Building2 : User;
  const settingsRoute = isEmployer ? ROUTES.employerSettings : ROUTES.settings;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          aria-label="Account menu"
          className="flex items-center gap-2 rounded-full p-1 transition-colors hover:bg-(--color-muted) focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--color-ring)"
        >
          <Avatar size="sm">
            <AvatarImage src={undefined} alt={name} />
            <AvatarFallback name={name} />
          </Avatar>
          <ChevronDown className="me-1 h-4 w-4 text-(--color-muted-foreground)" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="space-y-0.5">
            <p className="text-sm font-medium text-(--color-foreground)">{name}</p>
            <p className="truncate text-xs font-normal normal-case tracking-normal text-(--color-muted-foreground)">
              {user?.email}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate(profileRoute)}>
          <ProfileIcon className="h-4 w-4" /> {profileLabel}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => navigate(settingsRoute)}>
          <Settings className="h-4 w-4" /> Settings
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => logout.mutate()}>
          <LogOut className="h-4 w-4" /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function UserBlock() {
  const user = useUser();
  const name =
    [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.email || '—';
  return (
    <div className="border-t border-(--color-border) p-4">
      <div className="flex items-center gap-3">
        <Avatar size="sm">
          <AvatarFallback name={name} />
        </Avatar>
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{name}</p>
          <p className="truncate text-xs text-(--color-muted-foreground)">{user?.email}</p>
        </div>
      </div>
    </div>
  );
}
